import {inject, Injectable} from '@angular/core';
import {forkJoin, from, Observable, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

import {DirectusService, SubcriteriaAttributeDirectus} from '@frontend/directus';
import {
  EvaluationSubCriteriaAttributeControllerService,
  EvaluationSubCriteriaAttributeView,
  TenderPhaseType,
  UpsertEvaluationSubCriteriaAttributeRequest,
} from '@frontend/shared-api';
import {FixedValueService} from '../../services/fixed-value.service';
import {
  SubCriteriaAttributeItem,
  AttributesBySubCriteriaMap,
} from './subcriteria-attribute-evaluation.types';
import {SubCriteriaConfig} from '../../model/sub-criteria.interfaces';

const COLLECTION_BASE_NAME = 'subcriteria_attribute';

export interface ISubCriteriaAttributeEvaluationService {
  loadBatchSubCriteriaAttributesForEvaluation?(
    projectId: number,
    subCriteriaConfigs: SubCriteriaConfig[],
    collectionPostfix?: string,
    userEmail?: string | null,
  ): Observable<AttributesBySubCriteriaMap>;

  saveSubCriteriaAttributeEvaluations(
    projectId: number,
    evaluationCriteriaId: number,
    subCriteriaId: number,
    attributeEvaluationRequest: UpsertEvaluationSubCriteriaAttributeRequest,
    type: TenderPhaseType,
  ): any;
}

@Injectable({providedIn: 'root'})
export class SubCriteriaAttributeEvaluationService implements ISubCriteriaAttributeEvaluationService {
  private readonly directusService = inject(DirectusService);
  private readonly fixedValueService = inject(FixedValueService);
  private readonly attributeController = inject(EvaluationSubCriteriaAttributeControllerService);

  loadBatchSubCriteriaAttributesForEvaluation(
    projectId: number,
    subCriteriaConfigs: SubCriteriaConfig[],
    collectionPostfix?: string,
    userEmail: string | null = null,
  ): Observable<AttributesBySubCriteriaMap> {
    if (subCriteriaConfigs.length === 0) {
      return of({});
    }

    const validConfigs = subCriteriaConfigs.filter((config) => config.subCriteriaData?.directusId);

    if (validConfigs.length === 0) {
      return of({});
    }

    const collectionConfigs = validConfigs.map((config) => ({
      collectionName: `${COLLECTION_BASE_NAME}_${collectionPostfix ? collectionPostfix + '_' : ''}${config.subCriteriaId}`,
      loadFunction: () =>
        from(
          this.directusService.loadSubCriteriaAttributesBySubCriteria(
            config.subCriteriaData!.directusId,
          ),
        ),
    }));

    return this.fixedValueService
      .loadOrInitializeFixedValuesBatch(projectId, collectionConfigs)
      .pipe(
        switchMap((batchStoredIds) =>
          this.processValidConfigs(
            validConfigs,
            batchStoredIds,
            collectionPostfix,
            userEmail ?? undefined,
          ),
        ),
      );
  }

  private processValidConfigs(
    validConfigs: SubCriteriaConfig[],
    batchStoredIds: Record<string, string[]>,
    collectionPostfix?: string,
    userEmail?: string,
  ): Observable<AttributesBySubCriteriaMap> {
    const loadTasks = validConfigs.map((config) =>
      this.createLoadTaskForConfig(config, batchStoredIds, collectionPostfix, userEmail),
    );

    return forkJoin(loadTasks).pipe(
      map((results) => {
        const finalResults: AttributesBySubCriteriaMap = {};
        results.forEach((result) => {
          finalResults[result.subCriteriaId] = result.attributes;
        });
        return finalResults;
      }),
    );
  }

  private createLoadTaskForConfig(
    config: SubCriteriaConfig,
    batchStoredIds: Record<string, string[]>,
    collectionPostfix?: string,
    userEmail?: string,
  ): Observable<{subCriteriaId: number; attributes: SubCriteriaAttributeItem[]}> {
    const collectionName = `${COLLECTION_BASE_NAME}_${collectionPostfix ? collectionPostfix + '_' : ''}${config.subCriteriaId}`;
    const storedIds = batchStoredIds[collectionName] || [];
    const existingAttributes = config.subCriteriaData?.attributes || [];

    return from(
      this.directusService.loadSubCriteriaAttributesBySubCriteria(
        config.subCriteriaData!.directusId,
      ),
    ).pipe(
      map((allSubCriteriaAttributes) =>
        this.processSubCriteriaAttributes(
          config.subCriteriaId,
          config.evaluationCriteriaNumber,
          allSubCriteriaAttributes,
          storedIds,
          existingAttributes,
          userEmail,
          config.subCriteriaData?.scNumber,
        ),
      ),
    );
  }

  private processSubCriteriaAttributes(
    subCriteriaId: number,
    ecNumber: number,
    allSubCriteriaAttributes: SubcriteriaAttributeDirectus[],
    storedIds: string[],
    existingAttributes: EvaluationSubCriteriaAttributeView[],
    userEmail?: string,
    scNumber?: number,
  ): {subCriteriaId: number; attributes: SubCriteriaAttributeItem[]} {
    const filteredAttributes = this.filterDirectusSubCriteriaByProjectStoredIds(
      allSubCriteriaAttributes,
      storedIds,
    );

    const attributes: SubCriteriaAttributeItem[] = filteredAttributes.map((criteriaAttribute) =>
      this.createSubCriteriaAttributeItem(
        criteriaAttribute,
        existingAttributes,
        userEmail,
        ecNumber,
        scNumber,
      ),
    );

    return {subCriteriaId, attributes};
  }

  private createSubCriteriaAttributeItem(
    criteriaAttribute: SubcriteriaAttributeDirectus,
    existingAttributes: EvaluationSubCriteriaAttributeView[],
    userEmail?: string,
    ecNumber?: number,
    scNumber?: number,
  ): SubCriteriaAttributeItem {
    const existingAttributeEvaluation = existingAttributes.find(
      (attr) => attr.directusId === criteriaAttribute.id,
    );

    let selectedImportanceScore = undefined;
    if (existingAttributeEvaluation?.importanceScores) {
      const importanceScoresArray = existingAttributeEvaluation.importanceScores;

      if (userEmail) {
        const userScoreResponse = importanceScoresArray.find(
          (scoreResponse) => scoreResponse.createdBy === userEmail,
        );
        selectedImportanceScore = userScoreResponse?.importanceScore;
      } else {
        selectedImportanceScore = importanceScoresArray[0]?.importanceScore;
      }
    }

    return {
      id: existingAttributeEvaluation?.id,
      directusId: criteriaAttribute.id,
      ecNumber: ecNumber,
      scNumber: scNumber,
      title: criteriaAttribute.id,
      translations: criteriaAttribute.translations,
      selectedImportanceScore,
      scaNumber: existingAttributeEvaluation?.scaNumber ?? criteriaAttribute.sca_number,
      hasChanges: false,
      isSaving: false,
      consistency: existingAttributeEvaluation?.consistency,
    };
  }

  saveSubCriteriaAttributeEvaluations(
    projectId: number,
    evaluationCriteriaId: number,
    subCriteriaId: number,
    attributeEvaluationRequest: UpsertEvaluationSubCriteriaAttributeRequest,
    type: TenderPhaseType,
  ) {
    const requestParams = {
      projectId,
      evaluationCriteriaId,
      subCriteriaId,
      upsertEvaluationSubCriteriaAttributeRequest: attributeEvaluationRequest,
    };

    switch (type) {
      case TenderPhaseType.RFP:
        return this.attributeController.upsertRfpEvaluationSubCriteriaAttribute(requestParams);
      case TenderPhaseType.RFI:
        return this.attributeController.upsertRfiEvaluationSubCriteriaAttribute(requestParams);
      default:
        throw new Error(`Unsupported tender phase type: ${type}`);
    }
  }

  private filterDirectusSubCriteriaByProjectStoredIds(
    allDirectusSubCriteria: SubcriteriaAttributeDirectus[],
    projectStoredIds: string[],
  ): SubcriteriaAttributeDirectus[] {
    if (!projectStoredIds || projectStoredIds.length === 0) {
      return allDirectusSubCriteria;
    }
    return allDirectusSubCriteria.filter((criteria) => projectStoredIds.includes(criteria.id));
  }
}
